import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  IconSearch,
  IconX,
  IconInfoCircle,
  IconPlus,
  IconTrash,
  IconDeviceFloppy,
} from '@tabler/icons-react';
import {
  useGetRoleManagementMatrix,
  getGetRoleManagementMatrixQueryKey,
  getGetRoleManagementRolesQueryKey,
  usePutRoleManagementRolesIdPermissions,
  usePostRoleManagementRoles,
  useDeleteRoleManagementRolesId,
} from '../../api/role-management-api/role-management-api';
import {
  useGetAdminTenantsTenantIdMatrix,
  getGetAdminTenantsTenantIdMatrixQueryKey,
  getGetAdminTenantsTenantIdRolesQueryKey,
  usePutAdminTenantsTenantIdRolesRoleIdPermissions,
  usePostAdminTenantsTenantIdRoles,
  useDeleteAdminTenantsTenantIdRolesRoleId,
} from '../../api/admin-api/admin-api';
import { AccessLevel, PermissionScope, PermissionKey } from '../../api/model';
import { useNotifications } from '../../hooks/useNotifications';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import styles from './RolePermissionsMatrix.module.scss';

type PermCode = 'V' | 'B' | 'L' | 'E' | 'F' | '-';

const LEGEND_CODES: PermCode[] = ['V', 'B', 'L', 'E', 'F', '-'];

const LEVEL_CODE: Record<AccessLevel, PermCode> = {
  [AccessLevel.FULL]: 'V',
  [AccessLevel.EDIT]: 'B',
  [AccessLevel.READ]: 'L',
  [AccessLevel.CREATE]: 'E',
  [AccessLevel.RELEASE]: 'F',
  [AccessLevel.NONE]: '-',
};

// editor order
const LEVEL_OPTIONS: AccessLevel[] = [
  AccessLevel.FULL,
  AccessLevel.EDIT,
  AccessLevel.READ,
  AccessLevel.CREATE,
  AccessLevel.RELEASE,
  AccessLevel.NONE,
];

const SCOPE_NOTE: Partial<Record<PermissionScope, string>> = {
  [PermissionScope.GLOBAL]: 'global',
  [PermissionScope.PROJECT]: 'project',
  [PermissionScope.QS]: 'qa',
  [PermissionScope.PERSONAL]: 'personal',
  [PermissionScope.LIMITED]: 'limited',
  [PermissionScope.PORTFOLIO]: 'portfolio',
  [PermissionScope.OWN]: 'own',
  [PermissionScope.ASSIGNED]: 'assigned',
  [PermissionScope.PLANNED]: 'planned',
};

const SCOPE_OPTIONS: PermissionScope[] = [
  PermissionScope.UNLIMITED,
  PermissionScope.GLOBAL,
  PermissionScope.PORTFOLIO,
  PermissionScope.PROJECT,
  PermissionScope.QS,
  PermissionScope.LIMITED,
  PermissionScope.PERSONAL,
  PermissionScope.OWN,
  PermissionScope.ASSIGNED,
  PermissionScope.PLANNED,
];

const CATEGORY_ORDER = ['access', 'structure', 'quality', 'planning', 'customization', 'system'] as const;
type CategoryKey = (typeof CATEGORY_ORDER)[number];

const KEY_CATEGORY: Record<PermissionKey, CategoryKey> = {
  [PermissionKey.USER_MANAGEMENT]: 'access',
  [PermissionKey.ASSIGN_ROLES]: 'access',
  [PermissionKey.ADD_ROLES]: 'access',
  [PermissionKey.GLOBAL_PERMISSIONS]: 'access',
  [PermissionKey.MANAGE_TENANT]: 'access',
  [PermissionKey.CREATE_PROJECT]: 'structure',
  [PermissionKey.EDIT_PROJECT_STRUCTURE]: 'structure',
  [PermissionKey.PHASES_MILESTONES]: 'structure',
  [PermissionKey.DELIVERABLES]: 'structure',
  [PermissionKey.TASKS]: 'structure',
  [PermissionKey.CONFIGURE_PRELOAD_GLOBAL]: 'structure',
  [PermissionKey.APPLY_PRELOAD_PROJECT]: 'structure',
  [PermissionKey.EDIT_CHECKPOINTS]: 'quality',
  [PermissionKey.EDIT_AUDIT_PLANS]: 'quality',
  [PermissionKey.DOCUMENT_AUDIT_RESULTS]: 'quality',
  [PermissionKey.RELEASE_AUDIT_PLANS]: 'quality',
  [PermissionKey.MANAGE_DEPENDENCIES]: 'planning',
  [PermissionKey.TRIGGER_SCHEDULING]: 'planning',
  [PermissionKey.CREATE_ACTION_ITEMS]: 'planning',
  [PermissionKey.ASSIGN_ACTION_ITEMS]: 'planning',
  [PermissionKey.CREATE_RISK_MATRIX]: 'planning',
  [PermissionKey.CAPTURE_RISKS]: 'planning',
  [PermissionKey.PLAN_RESOURCES]: 'planning',
  [PermissionKey.CAPTURE_OWN_EFFORT]: 'planning',
  [PermissionKey.REVIEW_RELEASE_EFFORT]: 'planning',
  [PermissionKey.SAVE_TEMPLATES]: 'customization',
  [PermissionKey.CUSTOMIZE_ENTRY_SCREEN]: 'customization',
  [PermissionKey.CONFIGURE_REPORTING]: 'customization',
  [PermissionKey.CUSTOMIZE_COLORS]: 'customization',
  [PermissionKey.INTERFACES_API]: 'system',
  [PermissionKey.VIEW_AUDIT_TRAIL]: 'system',
  [PermissionKey.EDIT_AUDIT_TRAIL]: 'system',
};

const codeClass: Record<PermCode, string> = {
  V: styles.codeV,
  B: styles.codeB,
  L: styles.codeL,
  E: styles.codeE,
  F: styles.codeF,
  '-': styles.codeNone,
};

const ALL_KEYS = Object.values(PermissionKey) as PermissionKey[];

interface CellState {
  accessLevel: AccessLevel;
  scope: PermissionScope;
  optional: boolean;
}

// roleId
type Draft = Map<number, Map<PermissionKey, CellState>>;

interface MatrixLike {
  roles?: { roleId?: number }[];
  rows?: { permissionKey?: PermissionKey; cells?: { accessLevel?: AccessLevel; scope?: PermissionScope; optional?: boolean }[] }[];
}

// derives the editable draft
function buildDraft(matrix?: MatrixLike): Draft {
  const next: Draft = new Map();
  (matrix?.roles ?? []).forEach((role, idx) => {
    if (role.roleId == null) return;
    const inner = new Map<PermissionKey, CellState>();
    (matrix?.rows ?? []).forEach(row => {
      if (!row.permissionKey) return;
      const c = row.cells?.[idx];
      inner.set(row.permissionKey, {
        accessLevel: c?.accessLevel ?? AccessLevel.NONE,
        scope: c?.scope ?? PermissionScope.UNLIMITED,
        optional: c?.optional ?? false,
      });
    });
    next.set(role.roleId, inner);
  });
  return next;
}

interface EditingCell {
  roleId: number;
  key: PermissionKey;
  top: number;
  left: number;
  cellTop: number;
}

interface RolePermissionsMatrixProps {
  editable?: boolean;
  canManageRoles?: boolean;
  adminTenantId?: string;
}

const RolePermissionsMatrix: React.FC<RolePermissionsMatrixProps> = ({
  editable = false,
  canManageRoles = false,
  adminTenantId,
}) => {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();

  const [query, setQuery] = useState('');
  const [hoverCol, setHoverCol] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft>(new Map());
  const [dirtyRoles, setDirtyRoles] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ roleKey: '', displayName: '', description: '' });
  const [pendingDelete, setPendingDelete] = useState<{ roleId: number; name: string } | null>(null);

  const tenantMatrixQuery = useGetRoleManagementMatrix({
    query: { enabled: !adminTenantId, staleTime: 5 * 60 * 1000, retry: false },
  });
  const adminMatrixQuery = useGetAdminTenantsTenantIdMatrix(adminTenantId ?? '', {
    query: { enabled: !!adminTenantId, staleTime: 5 * 60 * 1000 },
  });
  const { data: matrix, isLoading, isError } = adminTenantId ? adminMatrixQuery : tenantMatrixQuery;

  const putPerms = usePutRoleManagementRolesIdPermissions();
  const putPermsAdmin = usePutAdminTenantsTenantIdRolesRoleIdPermissions();
  const postRole = usePostRoleManagementRoles();
  const postRoleAdmin = usePostAdminTenantsTenantIdRoles();
  const deleteRole = useDeleteRoleManagementRolesId();
  const deleteRoleAdmin = useDeleteAdminTenantsTenantIdRolesRoleId();

  const roles = useMemo(() => matrix?.roles ?? [], [matrix]);

  // build the editable draft whenever the server matrix changes
  useEffect(() => {
    if (!matrix) return;
    setDraft(buildDraft(matrix));
    setDirtyRoles(new Set());
    setEditing(null);
  }, [matrix]);

  const cellFor = (roleId?: number, key?: PermissionKey): CellState => {
    const fallback = { accessLevel: AccessLevel.NONE, scope: PermissionScope.UNLIMITED, optional: false };
    if (roleId == null || !key) return fallback;
    return draft.get(roleId)?.get(key) ?? fallback;
  };

  const legendKey = (code: PermCode) => (code === '-' ? 'none' : code);

  const scopeLabel = (scope: PermissionScope): string => {
    if (scope === PermissionScope.UNLIMITED) return t('user.permissions.scopes.unlimited', 'Unlimited');
    const noteKey = SCOPE_NOTE[scope];
    return noteKey ? t(`user.permissions.notes.${noteKey}`, noteKey) : scope;
  };

  // produces the displayed code
  const renderCell = (cell: CellState) => {
    const code = LEVEL_CODE[cell.accessLevel] ?? '-';
    if (code === '-') return { code, display: '–', note: '' };
    const notes: string[] = [];
    const noteKey = cell.scope ? SCOPE_NOTE[cell.scope] : undefined;
    if (noteKey) notes.push(t(`user.permissions.notes.${noteKey}`, noteKey));
    if (cell.optional) notes.push(t('user.permissions.notes.optional', 'optional'));
    return { code, display: code, note: notes.join(' ') };
  };

  const categories = useMemo(() => {
    const rows = (matrix?.rows ?? []).filter(r => !!r.permissionKey);
    const buckets = new Map<CategoryKey, { permissionKey: PermissionKey; label: string }[]>();
    rows.forEach(row => {
      const key = row.permissionKey as PermissionKey;
      const cat = KEY_CATEGORY[key] ?? 'system';
      const list = buckets.get(cat) ?? [];
      list.push({ permissionKey: key, label: row.permissionDisplayName || key });
      buckets.set(cat, list);
    });
    return CATEGORY_ORDER
      .map(key => ({ key, rows: buckets.get(key) ?? [] }))
      .filter(c => c.rows.length > 0);
  }, [matrix]);

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories
      .map(category => ({
        ...category,
        rows: category.rows.filter(row => row.label.toLowerCase().includes(q)),
      }))
      .filter(category => category.rows.length > 0);
  }, [query, categories]);

  const hasResults = filteredCategories.length > 0;


  const openEditor = (e: React.MouseEvent, roleId?: number, key?: PermissionKey) => {
    if (!editable || roleId == null || !key) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const width = 260;
    const left = Math.min(rect.left, window.innerWidth - width - 12);
    setEditing({ roleId, key, top: rect.bottom + 6, left: Math.max(12, left), cellTop: rect.top });
  };

  // After the editor mounts, flip it above the cell if it clips the viewport bottom.
  useEffect(() => {
    if (!editing || !editorRef.current) return;
    const editorHeight = editorRef.current.offsetHeight;
    if (editing.top + editorHeight > window.innerHeight - 8) {
      const topAbove = Math.max(8, editing.cellTop - editorHeight - 6);
      setEditing(prev => prev ? { ...prev, top: topAbove } : null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.roleId, editing?.key]);

  const patchCell = (roleId: number, key: PermissionKey, patch: Partial<CellState>) => {
    setDraft(prev => {
      const next = new Map(prev);
      const inner = new Map(next.get(roleId) ?? new Map<PermissionKey, CellState>());
      const current = inner.get(key) ?? { accessLevel: AccessLevel.NONE, scope: PermissionScope.UNLIMITED, optional: false };
      inner.set(key, { ...current, ...patch });
      next.set(roleId, inner);
      return next;
    });
    setDirtyRoles(prev => new Set(prev).add(roleId));
  };

  const discard = () => {
    setDraft(buildDraft(matrix));
    setDirtyRoles(new Set());
    setEditing(null);
  };

  const save = async () => {
    if (dirtyRoles.size === 0) return;
    setSaving(true);
    let anyFailed = false;
    for (const roleId of Array.from(dirtyRoles)) {
      const inner = draft.get(roleId);
      if (!inner) continue;
      const data = ALL_KEYS.map(key => {
        const c = inner.get(key) ?? { accessLevel: AccessLevel.NONE, scope: PermissionScope.UNLIMITED, optional: false };
        return { permissionKey: key, accessLevel: c.accessLevel, scope: c.scope, optional: c.optional };
      });
      try {
        if (adminTenantId) {
          await putPermsAdmin.mutateAsync({ tenantId: adminTenantId, roleId, data });
        } else {
          await putPerms.mutateAsync({ id: roleId, data });
        }
        setDirtyRoles(prev => { const n = new Set(prev); n.delete(roleId); return n; });
      } catch {
        const roleName = roles.find(r => r.roleId === roleId)?.displayName ?? String(roleId);
        showError(
          t('user.permissions.edit.saveError', 'Could not save permissions.'),
          t('user.permissions.edit.saveErrorRole', { role: roleName, defaultValue: '"{{role}}" could not be saved.' })
        );
        anyFailed = true;
      }
    }
    if (!anyFailed) {
      showSuccess(t('user.permissions.edit.saved', 'Permissions saved'));
    }
    if (adminTenantId) {
      queryClient.invalidateQueries({ queryKey: getGetAdminTenantsTenantIdMatrixQueryKey(adminTenantId) });
    } else {
      queryClient.invalidateQueries({ queryKey: getGetRoleManagementMatrixQueryKey() });
    }
    setSaving(false);
  };

  // role add / delete

  const submitAddRole = (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.roleKey.trim() || !addForm.displayName.trim()) return;
    const roleData = {
      roleKey: addForm.roleKey.trim(),
      displayName: addForm.displayName.trim(),
      description: addForm.description.trim() || undefined,
    };
    const onSuccess = () => {
      showSuccess(t('user.permissions.roleAdd.created', 'Role created'));
      setAddOpen(false);
      setAddForm({ roleKey: '', displayName: '', description: '' });
      if (adminTenantId) {
        queryClient.invalidateQueries({ queryKey: getGetAdminTenantsTenantIdMatrixQueryKey(adminTenantId) });
        queryClient.invalidateQueries({ queryKey: getGetAdminTenantsTenantIdRolesQueryKey(adminTenantId) });
      } else {
        queryClient.invalidateQueries({ queryKey: getGetRoleManagementMatrixQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetRoleManagementRolesQueryKey() });
      }
    };
    const onError = () => showError(t('user.permissions.roleAdd.createError', 'Could not create the role.'));
    if (adminTenantId) {
      postRoleAdmin.mutate({ tenantId: adminTenantId, data: roleData }, { onSuccess, onError });
    } else {
      postRole.mutate({ data: roleData }, { onSuccess, onError });
    }
  };

  const confirmDeleteRole = () => {
    const target = pendingDelete;
    if (!target) return;
    const onSuccess = () => {
      showSuccess(t('user.permissions.roleDelete.deleted', 'Role deleted'));
      if (adminTenantId) {
        queryClient.invalidateQueries({ queryKey: getGetAdminTenantsTenantIdMatrixQueryKey(adminTenantId) });
        queryClient.invalidateQueries({ queryKey: getGetAdminTenantsTenantIdRolesQueryKey(adminTenantId) });
      } else {
        queryClient.invalidateQueries({ queryKey: getGetRoleManagementMatrixQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetRoleManagementRolesQueryKey() });
      }
    };
    const onError = () => showError(t('user.permissions.roleDelete.deleteError', 'Could not delete the role.'));
    if (adminTenantId) {
      deleteRoleAdmin.mutate({ tenantId: adminTenantId, roleId: target.roleId }, { onSuccess, onError });
    } else {
      deleteRole.mutate({ id: target.roleId }, { onSuccess, onError });
    }
    setPendingDelete(null);
  };

  const editingCellState = editing ? cellFor(editing.roleId, editing.key) : null;

  return (
    <div className={styles.matrix}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('user.permissions.title')}</h2>
        <p className={styles.description}>{t('user.permissions.description')}</p>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <IconSearch size={16} className={styles.searchIcon} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('user.permissions.searchPlaceholder')}
            className={styles.searchInput}
          />
          {query && (
            <button
              type="button"
              className={styles.clearSearch}
              onClick={() => setQuery('')}
              aria-label={t('user.permissions.clearSearch')}
            >
              <IconX size={14} />
            </button>
          )}
        </div>

        {canManageRoles && (
          <button type="button" className={styles.addRoleButton} onClick={() => setAddOpen(true)}>
            <IconPlus size={16} />
            {t('user.permissions.roleAdd.button', 'Add role')}
          </button>
        )}

        <div className={styles.infoTip} tabIndex={0} aria-label={t('user.permissions.legendTitle')}>
          <IconInfoCircle size={18} />
          <div className={styles.infoPopover} role="tooltip">
            <span className={styles.infoPopoverTitle}>{t('user.permissions.legendTitle')}</span>
            {LEGEND_CODES.map((code) => (
              <div key={code} className={styles.infoPopoverItem}>
                <span className={`${styles.badge} ${codeClass[code]}`}>{code === '-' ? '–' : code}</span>
                <span className={styles.infoPopoverText}>
                  {t(`user.permissions.legend.${legendKey(code)}.title`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {editable && (
        <p className={styles.editHint}>{t('user.permissions.edit.hint', 'Click a cell to change a role’s access.')}</p>
      )}

      <div className={styles.tableWrapper}>
        {isLoading ? (
          <div className={styles.stateMessage}>{t('user.permissions.loading', 'Loading permission matrix…')}</div>
        ) : isError ? (
          <div className={styles.stateMessage}>{t('user.permissions.loadError', 'Could not load the permission matrix.')}</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.areaHead}>{t('user.permissions.functionalArea')}</th>
                {roles.map((role, idx) => (
                  <th
                    key={role.roleId ?? idx}
                    className={`${styles.roleHead} ${hoverCol === idx ? styles.colActive : ''}`}
                    onMouseEnter={() => setHoverCol(idx)}
                    onMouseLeave={() => setHoverCol(null)}
                  >
                    <span className={styles.roleHeadInner}>
                      {role.displayName}
                      {canManageRoles && role.roleId != null && (
                        <button
                          type="button"
                          className={styles.roleDeleteButton}
                          onClick={() => setPendingDelete({ roleId: role.roleId!, name: role.displayName ?? role.roleKey ?? '' })}
                          aria-label={t('user.permissions.roleDelete.button', 'Delete role')}
                          title={t('user.permissions.roleDelete.button', 'Delete role')}
                        >
                          <IconTrash size={13} />
                        </button>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hasResults ? (
                filteredCategories.map((category) => (
                  <React.Fragment key={category.key}>
                    <tr className={styles.categoryRow}>
                      <td className={styles.categoryCell} colSpan={roles.length + 1}>
                        {t(`user.permissions.categories.${category.key}`)}
                      </td>
                    </tr>
                    {category.rows.map((row) => (
                      <tr key={row.permissionKey} className={styles.dataRow}>
                        <td className={styles.areaCell}>{row.label}</td>
                        {roles.map((role, idx) => {
                          const cell = renderCell(cellFor(role.roleId, row.permissionKey));
                          const isOpen = editing?.roleId === role.roleId && editing?.key === row.permissionKey;
                          return (
                            <td
                              key={idx}
                              className={`${styles.permCell} ${hoverCol === idx ? styles.colActive : ''} ${editable ? styles.permCellEditable : ''} ${isOpen ? styles.permCellOpen : ''}`}
                              onMouseEnter={() => setHoverCol(idx)}
                              onMouseLeave={() => setHoverCol(null)}
                              onClick={(e) => openEditor(e, role.roleId, row.permissionKey)}
                            >
                              <span
                                className={`${styles.badge} ${codeClass[cell.code]}`}
                                title={cell.note ? `${cell.display} — ${cell.note}` : undefined}
                              >
                                {cell.display}
                              </span>
                              {cell.note && <span className={styles.note}>{cell.note}</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td className={styles.emptyState} colSpan={roles.length + 1}>
                    {t('user.permissions.noMatch', { query })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.legend}>
        <span className={styles.legendHeading}>{t('user.permissions.legendTitle')}</span>
        <div className={styles.legendGrid}>
          {LEGEND_CODES.map((code) => (
            <div key={code} className={styles.legendItem}>
              <span className={`${styles.badge} ${codeClass[code]}`}>{code === '-' ? '–' : code}</span>
              <div className={styles.legendText}>
                <span className={styles.legendTitle}>{t(`user.permissions.legend.${legendKey(code)}.title`)}</span>
                <span className={styles.legendDesc}>{t(`user.permissions.legend.${legendKey(code)}.desc`)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && editingCellState && (
        <>
          <div className={styles.editorBackdrop} onClick={() => setEditing(null)} />
          <div ref={editorRef} className={styles.cellEditor} style={{ top: editing.top, left: editing.left }}>
            <span className={styles.cellEditorLabel}>{t('user.permissions.edit.level', 'Access level')}</span>
            <div className={styles.levelRow}>
              {LEVEL_OPTIONS.map(level => {
                const code = LEVEL_CODE[level];
                const active = editingCellState.accessLevel === level;
                return (
                  <button
                    key={level}
                    type="button"
                    className={`${styles.levelOption} ${codeClass[code]} ${active ? styles.levelOptionActive : ''}`}
                    onClick={() => patchCell(editing.roleId, editing.key, { accessLevel: level })}
                    title={t(`user.permissions.legend.${legendKey(code)}.title`)}
                  >
                    {code === '-' ? '–' : code}
                  </button>
                );
              })}
            </div>

            {editingCellState.accessLevel !== AccessLevel.NONE && (
              <>
                <span className={styles.cellEditorLabel}>{t('user.permissions.edit.scope', 'Scope')}</span>
                <select
                  className={styles.scopeSelect}
                  value={editingCellState.scope}
                  onChange={(e) => patchCell(editing.roleId, editing.key, { scope: e.target.value as PermissionScope })}
                >
                  {SCOPE_OPTIONS.map(scope => (
                    <option key={scope} value={scope}>{scopeLabel(scope)}</option>
                  ))}
                </select>

                <label className={styles.optionalToggle}>
                  <input
                    type="checkbox"
                    checked={editingCellState.optional}
                    onChange={(e) => patchCell(editing.roleId, editing.key, { optional: e.target.checked })}
                  />
                  {t('user.permissions.edit.optional', 'Optional right')}
                </label>
              </>
            )}

            <button type="button" className={styles.cellEditorDone} onClick={() => setEditing(null)}>
              {t('user.permissions.edit.done', 'Done')}
            </button>
          </div>
        </>
      )}

      {editable && dirtyRoles.size > 0 && (
        <div className={styles.saveBar}>
          <span className={styles.saveBarText}>
            {t('user.permissions.edit.unsaved', { count: dirtyRoles.size })}
          </span>
          <div className={styles.saveBarActions}>
            <button type="button" className={styles.discardButton} onClick={discard} disabled={saving}>
              {t('user.permissions.edit.discard', 'Discard')}
            </button>
            <button type="button" className={styles.saveButton} onClick={save} disabled={saving}>
              <IconDeviceFloppy size={16} />
              {saving ? t('user.permissions.edit.saving', 'Saving…') : t('user.permissions.edit.save', 'Save changes')}
            </button>
          </div>
        </div>
      )}

      {addOpen && (
        <div className={styles.dialogOverlay} onClick={() => setAddOpen(false)}>
          <form className={styles.dialog} onClick={(e) => e.stopPropagation()} onSubmit={submitAddRole}>
            <div className={styles.dialogHeader}>
              <h3 className={styles.dialogTitle}>{t('user.permissions.roleAdd.title', 'Add a new role')}</h3>
              <button type="button" className={styles.dialogClose} onClick={() => setAddOpen(false)} aria-label={t('user.permissions.roleAdd.cancel', 'Cancel')}>
                <IconX size={18} />
              </button>
            </div>

            <div className={styles.dialogField}>
              <label htmlFor="roleDisplayName">{t('user.permissions.roleAdd.name', 'Display name')} <span className={styles.required}>*</span></label>
              <input
                id="roleDisplayName"
                type="text"
                value={addForm.displayName}
                onChange={(e) => setAddForm(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder={t('user.permissions.roleAdd.namePlaceholder', 'e.g. External Auditor')}
              />
            </div>

            <div className={styles.dialogField}>
              <label htmlFor="roleKey">{t('user.permissions.roleAdd.key', 'Role key')} <span className={styles.required}>*</span></label>
              <input
                id="roleKey"
                type="text"
                value={addForm.roleKey}
                onChange={(e) => setAddForm(prev => ({ ...prev, roleKey: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') }))}
                placeholder={t('user.permissions.roleAdd.keyPlaceholder', 'e.g. EXTERNAL_AUDITOR')}
              />
              <span className={styles.dialogHint}>{t('user.permissions.roleAdd.keyHint', 'Unique identifier. Uppercase, no spaces.')}</span>
            </div>

            <div className={styles.dialogField}>
              <label htmlFor="roleDescription">{t('user.permissions.roleAdd.description', 'Description')}</label>
              <textarea
                id="roleDescription"
                rows={2}
                value={addForm.description}
                onChange={(e) => setAddForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('user.permissions.roleAdd.descriptionPlaceholder', 'What is this role for?')}
              />
            </div>

            <div className={styles.dialogActions}>
              <button type="button" className={styles.discardButton} onClick={() => setAddOpen(false)} disabled={postRole.isPending}>
                {t('user.permissions.roleAdd.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                className={styles.saveButton}
                disabled={postRole.isPending || !addForm.roleKey.trim() || !addForm.displayName.trim()}
              >
                {postRole.isPending ? t('user.permissions.roleAdd.creating', 'Creating…') : t('user.permissions.roleAdd.submit', 'Create role')}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmationDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDeleteRole}
        type="danger"
        title={t('user.permissions.roleDelete.confirmTitle', 'Delete role?')}
        message={t('user.permissions.roleDelete.confirmMessage', {
          role: pendingDelete?.name ?? '',
          defaultValue: 'Delete the role {{role}}? Users currently assigned to it will lose its access.',
        })}
        confirmText={t('user.permissions.roleDelete.confirm', 'Delete role')}
        cancelText={t('user.permissions.roleAdd.cancel', 'Cancel')}
      />
    </div>
  );
};

export default RolePermissionsMatrix;
